import type { Lead, ResearchedContact } from '@/types/research';

function normalize(value: unknown): string {
  return String(value ?? '').trim().toLowerCase();
}

function fnv1a(input: string): string {
  let hash = 0x811c9dc5;
  for (let i = 0; i < input.length; i += 1) {
    hash ^= input.charCodeAt(i);
    hash += (hash << 1) + (hash << 4) + (hash << 7) + (hash << 8) + (hash << 24);
  }
  return (hash >>> 0).toString(16);
}

function getLeadCompanyName(lead: Partial<Lead>): string {
  if (typeof lead.company === 'string') return lead.company;
  return lead.company?.name || lead.companyName || lead.Company || '';
}

export function buildStableLeadResearchKeys(lead: Partial<Lead> | null | undefined): string[] {
  if (!lead) return [];

  const keys = new Set<string>();
  const pushRaw = (value: unknown) => {
    const key = String(value ?? '').trim();
    if (key) keys.add(key);
  };

  pushRaw(lead.searchResultId);

  const linkedin = normalize(lead.linkedInUrl || lead.liUrl);
  if (linkedin) keys.add(`li_${fnv1a(linkedin)}`);

  const email = normalize(lead.email || lead.businessEmail1 || lead.personalEmail1);
  if (email) keys.add(`em_${fnv1a(email)}`);

  const firstName = String(lead.firstName ?? '').trim();
  const lastName = String(lead.lastName ?? '').trim();
  const fullName = String(lead.name ?? `${firstName} ${lastName}`.trim()).trim();
  const company = getLeadCompanyName(lead);
  const nc = `${normalize(fullName)}|${normalize(company)}`;
  if (nc !== '|') keys.add(`nc_${fnv1a(nc)}`);

  return Array.from(keys);
}

export function buildStableContactResearchKeys(contact: ResearchedContact | null | undefined): string[] {
  if (!contact) return [];

  const keys = new Set<string>();
  const pushRaw = (value: unknown) => {
    const key = String(value ?? '').trim();
    if (key) keys.add(key);
  };

  pushRaw((contact as { searchResultId?: unknown }).searchResultId);
  pushRaw((contact as { sourceSearchResultId?: unknown }).sourceSearchResultId);

  const linkedin = normalize(
    (contact as { LIProfileUrl?: unknown }).LIProfileUrl ??
    (contact as { linkedInUrl?: unknown }).linkedInUrl ??
    (contact as { liUrl?: unknown }).liUrl
  );
  if (linkedin) keys.add(`li_${fnv1a(linkedin)}`);

  const email = normalize(
    (contact as { Email?: unknown }).Email ??
    (contact as { email?: unknown }).email ??
    (contact as { email1?: unknown }).email1 ??
    (contact as { PersonalEmail?: unknown }).PersonalEmail
  );
  if (email) keys.add(`em_${fnv1a(email)}`);

  const fullName = String(
    (contact as { fullName?: unknown }).fullName ??
    (contact as { Name?: unknown }).Name ??
    `${String((contact as { firstName?: unknown }).firstName ?? '').trim()} ${String((contact as { lastName?: unknown }).lastName ?? '').trim()}`.trim()
  ).trim();
  const company = String(
    (contact as { Company?: unknown }).Company ??
    (contact as { company?: unknown }).company ??
    (contact as { companyName?: unknown }).companyName
  ).trim();
  const nc = `${normalize(fullName)}|${normalize(company)}`;
  if (nc !== '|') keys.add(`nc_${fnv1a(nc)}`);

  return Array.from(keys);
}

export function findMatchingResearchedContact(
  lead: Partial<Lead> | null | undefined,
  researchedContacts: Record<string, ResearchedContact>
): ResearchedContact | null {
  if (!lead) return null;
  for (const key of buildStableLeadResearchKeys(lead)) {
    const match = researchedContacts[key];
    if (match) return match;
  }
  return null;
}
